import SimpleModal from "app/components/Modals/SimpleModal";
import Button from "app/storybookComponents/Button";
import SortableTable from "app/components/SortableTable";
import NoResultsIcon from "resources/images/results-not-found@2x.png";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import Select from "react-select";
import { RowContent } from "app/components/SortableTable/types";
import { Link } from "react-router-dom";
import { downloadCSV, getSelectProps } from "utils/helperFunctions";
import { Card, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/pro-regular-svg-icons";
import {
  selectSendWeeklyCheckInRemindersPerUserAccountIdStatus,
  selectSendWeeklyCheckInRemindersStatus,
  selectWeeklyCheckInUserStatus,
  sendWeeklyCheckInReminders,
} from "../slice";
import SendAllPendingCheckinReminderWarningModal from "./SendAllPendingCheckinReminderWarningModal";
import SendPendingReminderModal from "./SendPendingReminderModal";
import ReminderButton from "../ReminderButton";

interface Props {
  show: boolean;
  onShow: (show: boolean) => void;
  userAccountIds?: number[];
  modalDescription?: string;
}

const IncompleteCheckInsModal = ({
  show,
  onShow,
  userAccountIds = [],
  modalDescription = "People who have not completed their Check-In:",
}: Props) => {
  const dispatch = useAppDispatch();
  const [searchInput, setSearchInput] = useState("");
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const weeklyCheckInUserStatus = useAppSelector(selectWeeklyCheckInUserStatus);
  const sendEmailReminderStatus = useAppSelector(
    selectSendWeeklyCheckInRemindersStatus
  );
  const sendWeeklyCheckInRemindersPerUserAccountIdStatus = useAppSelector(
    selectSendWeeklyCheckInRemindersPerUserAccountIdStatus
  );
  const [isSendAllRemindersModalOpened, setIsSendAllRemindersModalOpened] =
    useState(false);
  const [
    sendReminderToIndividualConfirmationState,
    setSendReminderToIndividualConfirmationState,
  ] = useState<{
    userAccountId: number | null;
  }>({
    userAccountId: null,
  });

  const filteredUserAccountIds = useMemo(
    () =>
      userAccountIds.filter((userAccountId) => {
        const user = usersById[userAccountId];
        if (!user) return false;
        const fullName = `${user.firstName} ${user.lastName}`.trim();
        const { emailAddress } = user;
        if (
          searchInput &&
          !fullName.toLowerCase().includes(searchInput.toLowerCase()) &&
          !emailAddress.toLowerCase().includes(searchInput.toLowerCase())
        ) {
          return false;
        }

        return true;
      }),
    [userAccountIds, usersById, searchInput]
  );

  const haveAllPendingUsersBeenReminded = useMemo(() => {
    const result: { [userAccountId: number]: boolean } = {};
    const { usersWhoHaveBeenRemindedInTheLastFiveMinutes = [] } =
      weeklyCheckInUserStatus ?? {};
    usersWhoHaveBeenRemindedInTheLastFiveMinutes.forEach((userAccountId) => {
      result[userAccountId] = true;
    });

    const allUserHaveBeenReminded = !!filteredUserAccountIds?.every(
      (userAccountId) => result[userAccountId]
    );
    return allUserHaveBeenReminded;
  }, [weeklyCheckInUserStatus, filteredUserAccountIds]);

  const getTableRow = (userAccountId: number): RowContent => {
    const user = usersById[userAccountId];
    if (!user) {
      return {};
    }

    const isReminderLoading =
      sendWeeklyCheckInRemindersPerUserAccountIdStatus[userAccountId] ===
      "loading";
    const fullName =
      `${user.firstName} ${user.lastName}`.trim() ?? user.emailAddress;
    const jobTitle = user.jobTitle || "No job title";
    return {
      user: {
        displayValue: (
          <div>
            <Link to={`/UserGuide/${user.userAccountId}`}>
              <p>{fullName}</p>
            </Link>
            <p className="small-grey-text">{jobTitle}</p>
          </div>
        ),
        sortValue: fullName,
      },
      cta: {
        displayValue: (
          <Button
            variant="secondary-blue"
            onClick={() =>
              setSendReminderToIndividualConfirmationState({ userAccountId })
            }
            disabled={isReminderLoading}
          >
            Send Reminder
          </Button>
        ),
        sortValue: "",
        className: "send-reminder-cell",
      },
    };
  };

  const getCSVRows = () => {
    // return type should be like [{'User': 'John Doe', 'Job Title': 'No job title'}]
    const rows: { [headerKey: string]: string }[] = [];

    filteredUserAccountIds.forEach((userAccountId) => {
      const user = usersById[userAccountId];
      if (!user) return;
      const fullName = `${user.firstName} ${user.lastName}`.trim();
      const jobTitle = user.jobTitle || "No job title";
      rows.push({ User: fullName, "Job Title": jobTitle });
    });

    return rows;
  };

  const exportCSVFile = () => {
    const rows = getCSVRows();
    const data = [["User", "Job Title"]];
    rows.forEach((row) => {
      data.push(Object.values(row));
    });
    downloadCSV("check-in-incomplete-users", data);
  };

  const getTableHeaders = () => [
    {
      label: "User",
      key: "user",
      className: "user-name-cell",
    },
    {
      label: "",
      key: "cta",
      additionalContent: (
        <div className="row-gap-16px">
          <Button
            variant="secondary-gray"
            onClick={exportCSVFile}
            disabled={rows.length === 0}
          >
            <FontAwesomeIcon icon={["fasr", "download"]} className="me-2" />
            Export
          </Button>

          <ReminderButton
            disabled={sendEmailReminderStatus === "loading"}
            className="nowrap"
            reminderSent={haveAllPendingUsersBeenReminded}
            variant="secondary-blue"
            onClick={() => setIsSendAllRemindersModalOpened(true)}
            sendButtonText="Remind All"
            sentButtonText="Reminders Sent"
            prefixContent={
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
            }
          />
        </div>
      ),
    },
  ];

  const onRemindAll = async () => {
    await dispatch(sendWeeklyCheckInReminders(filteredUserAccountIds));
    setIsSendAllRemindersModalOpened(false);
    onShow(false);
  };

  const onSendReminder = async (userAccountId: number | null) => {
    if (!userAccountId) return;
    await dispatch(sendWeeklyCheckInReminders([userAccountId]));
    setSendReminderToIndividualConfirmationState({ userAccountId: null });
  };

  const getSearchInput = () => {
    const { selectStyles, components } = getSelectProps();
    return (
      <Form.Group>
        <Select
          placeholder="Search by name..."
          isClearable={true}
          isSearchable={true}
          components={components}
          inputValue={searchInput}
          styles={selectStyles}
          menuIsOpen={false}
          onInputChange={(e, actionMeta) => {
            if (actionMeta.action === "input-change") {
              setSearchInput(e);
            }
          }}
        />
      </Form.Group>
    );
  };

  const rows = filteredUserAccountIds.map(getTableRow);

  const getEmptyState = () => {
    if (rows.length !== 0) {
      return null;
    }
    let emptyStateText = "No incomplete check-ins found";
    let emptyStateBodyText =
      "Once there are incomplete check-ins, they will be shown here.";

    if (searchInput) {
      emptyStateText = "No results found";
      emptyStateBodyText = "Try searching with another name.";
    }

    return (
      <div style={{ marginTop: "-20px" }}>
        <Card className="not-available-card blue">
          <img
            src={NoResultsIcon}
            alt="no results"
            height="80px"
            width="80px"
          />
          <div className="column-gap-8px">
            <p className="fw-bold">{emptyStateText}</p>
            <p>{emptyStateBodyText}</p>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <>
      <SendAllPendingCheckinReminderWarningModal
        isOpen={isSendAllRemindersModalOpened}
        onHide={() => setIsSendAllRemindersModalOpened(false)}
        onConfirmClick={onRemindAll}
        peopleCount={userAccountIds?.length}
        isInProgress={sendEmailReminderStatus === "loading"}
      />
      <SendPendingReminderModal
        onHide={() =>
          setSendReminderToIndividualConfirmationState({ userAccountId: null })
        }
        onConfirmClick={() =>
          onSendReminder(
            sendReminderToIndividualConfirmationState.userAccountId
          )
        }
        isInProgress={sendEmailReminderStatus === "loading"}
        userAccountId={sendReminderToIndividualConfirmationState.userAccountId}
      />
      <SimpleModal
        show={
          show &&
          !isSendAllRemindersModalOpened &&
          !sendReminderToIndividualConfirmationState.userAccountId
        }
        onHide={() => onShow(false)}
        title="Incomplete Check-ins"
      >
        <hr className="m-0" />
        <p>{modalDescription}</p>
        {getSearchInput()}
        <SortableTable
          columnHeaders={getTableHeaders()}
          rows={rows}
          tableClassName="incomplete-check-ins-modal-table"
          hideHeaderLineWhenEmpty
        />
        {getEmptyState()}
      </SimpleModal>
    </>
  );
};

export default IncompleteCheckInsModal;
